import * as React from "react";
import { Box, Drawer, CssBaseline, AppBar, Toolbar, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, Divider, FormControl, Select } from "@mui/material";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import HistoryIcon from "@mui/icons-material/History";
import BadgeIcon from "@mui/icons-material/Badge";
import HelpIcon from "@mui/icons-material/Help";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import DeveloperModeIcon from "@mui/icons-material/DeveloperMode";
import Typography from "@mui/material/Typography";
import { WhitePenciledLogo } from "../styles/HeaderStyles";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Colors } from "../Colors";
import { useSelector } from "react-redux";
import { Agent, AgentType, EHR, FrontendUser, OnboardingStep, Organization } from "../types";
import { RootState, useAppDispatch } from "../store";
import { clearAgents, fetchAgents } from "../slices/AgentSlice";
import { useSnackbar } from "../providers/SnackbarProvider";
import { useEffect, useMemo, useState } from "react";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { getSession, signOut } from "../slices/AuthSlice";
import { usePostHog } from "posthog-js/react";
import axios, { AxiosError } from "axios";
import { dropdownStyles, menuPropsStyles } from "../styles/GeneralStyles";
import { fetchProviders as FetchPenciledProviders, fetchProviderGroups } from "../slices/ProviderSlice";
import SettingsIcon from "@mui/icons-material/Settings";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Assignment, Checklist, PersonAddAlt1, PlaylistAdd, InsertChartOutlined } from "@mui/icons-material";
import { fetchWebptProviders } from "../slices/WebptIntegrationSlice";

const drawerWidth = 300;

type NavItem = {
  key: string; // stable id
  label: string;
  path: string;
  icon: React.ReactNode;
  children?: NavItem[];
  showChildrenOnlyWhenSelected?: boolean;
  external?: boolean;
  // when present, marks this item selected for any path starting with these prefixes
  matchPrefixes?: string[];
  // when present, include item only if condition true
  when?: boolean;
};

const isExternal = (path: string) => path.startsWith("https://");

const DashboardSidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { showMessage } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const posthog = usePostHog();

  const penciledProviders = useSelector((state: RootState) => state.providers.providers);

  const user: FrontendUser | null = useSelector((state: RootState) => state.auth.user);
  const organization: Organization | null = useSelector((state: RootState) => state.auth.organization);
  const agents: Agent[] = useSelector((state: RootState) => state.agents.agents);
  const onboardingStep: OnboardingStep = useSelector((state: RootState) => state.auth.onboardingStep);

  const firstWaitlistAgent = agents.find((a) => a.agentType === AgentType.waitlist);
  const [dashboardPath, setDashboardPath] = useState<string>(`/dashboard/agents/${firstWaitlistAgent?.agentId}`);

  useEffect(() => {
    if (onboardingStep === OnboardingStep.end) {
      setDashboardPath(`/dashboard/agents/${firstWaitlistAgent?.agentId}`);
    } else {
      setDashboardPath(`/dashboard`);
    }
  }, [firstWaitlistAgent, onboardingStep]);

  const userId = user?.userId;
  const token = user?.token;

  const fetchGlobalAgents = async () => {
    if (userId && token) {
      try {
        await dispatch(fetchAgents({ userId, token }));
      } catch (error) {
        if ((error as AxiosError).response?.status === 401) {
          showMessage("Session has expired", "error");
        }
        console.error("Failed to fetch calls:", (error as Error).message);
      }
    } else {
      dispatch(clearAgents());
    }
  };

  useEffect(() => {
    fetchGlobalAgents();
  }, [user?.token]);

  useEffect(() => {
    if (!user?.token) return;

    if (organization?.integrations.includes(EHR.webpt)) {
      dispatch(fetchWebptProviders({ token: user.token, organizationId: user.organizationId }));

      if (penciledProviders.length === 0) {
        dispatch(FetchPenciledProviders(user.token));
        dispatch(fetchProviderGroups(user.token));
      }
    }
  }, [organization?.integrations]);

  const usingWebpt = organization?.ehr === EHR.webpt;

  const [mobileOpen, setMobileOpen] = useState(false);
  const toggleMobileDrawer = () => setMobileOpen((p) => !p);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const accountMenuOpen = Boolean(anchorEl);

  const handleAccountClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);

  const handleLogout = () => {
    sessionStorage.removeItem("sessionStartTime");
    dispatch(signOut());
    navigate("/login");
  };

  const handleAccountMenuClose = (action?: "account" | "logout") => {
    setAnchorEl(null);
    if (action === "account") navigate("/dashboard/account");
    if (action === "logout") handleLogout();
  };

  const handleOrgChange = async (organizationId: string) => {
    const org = user?.organizations.find((o) => o.organizationId === organizationId);
    if (!user?.token || !organizationId || !org) return;

    const superOrganizationId = org.superOrganizationId || undefined;

    await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/users/update`, { organizationId, superOrganizationId }, { headers: { Authorization: `Bearer ${user?.token}` } });

    await dispatch(getSession());
    window.location.reload();
  };

  // --------------------------
  // NAV CONFIG (single source)
  // --------------------------
  const navItems: NavItem[] = useMemo(() => {
    const items: NavItem[] = [
      {
        key: "patients",
        label: "Patients",
        path: "/dashboard/patients",
        icon: <DirectionsRunIcon />,
        matchPrefixes: ["/dashboard/patients"],
        showChildrenOnlyWhenSelected: true,
        children: [
          {
            key: "recommended-patients",
            label: "Recommended Patients",
            path: "/dashboard/patients/auto-add",
            icon: <PersonAddAlt1 />,
            matchPrefixes: ["/dashboard/patients/auto-add"],
          },
          {
            key: "auto-add-queue",
            label: "Auto-Add Queue",
            path: "/dashboard/patients/auto-add-queue",
            icon: <PlaylistAdd />,
            matchPrefixes: ["/dashboard/patients/auto-add-queue"],
          },
        ],
      },
      {
        key: "plan-of-care",
        label: "Plan of Care",
        path: "/dashboard/plan-of-care",
        icon: <Assignment />,
        matchPrefixes: ["/dashboard/plan-of-care"],
      },
      {
        key: "waitlist",
        label: "Waitlist",
        path: "/dashboard/agents",
        icon: <PendingActionsIcon />,
        matchPrefixes: ["/dashboard/agents"],
      },
      {
        key: "providers",
        label: "Providers",
        path: "/dashboard/providers",
        icon: <BadgeIcon />,
        matchPrefixes: ["/dashboard/providers"],
      },
      {
        key: "todo",
        label: "To Do",
        path: "/dashboard/todo",
        icon: <Checklist />,
        matchPrefixes: ["/dashboard/todo"],
      },
      {
        key: "reporting",
        label: "Reporting",
        path: "/dashboard/reporting",
        icon: <InsertChartOutlined />,
        matchPrefixes: ["/dashboard/reporting"],
      },
      {
        key: "logs",
        label: "Logs",
        path: "/dashboard/logs",
        icon: <HistoryIcon />,
        matchPrefixes: ["/dashboard/logs"],
      },
      // {
      //   key: "payers",
      //   label: "Payers",
      //   path: "/dashboard/payers",
      //   icon: <LibraryBooksIcon />,
      //   matchPrefixes: ["/dashboard/payers"],
      // },
    ];
    return items.filter((i) => i.when !== false);
  }, [user, usingWebpt]);

  const bottomNavItems: NavItem[] = useMemo(() => {
    const isSuperAdmin = !!user?.superOrganizationId && user?.superOrgIds?.includes(user?.superOrganizationId);

    const items: NavItem[] = [
      {
        key: "admin",
        label: "Admin",
        path: "/dashboard/admin",
        icon: <ManageAccountsOutlinedIcon />,
        matchPrefixes: ["/dashboard/admin"],
        when: isSuperAdmin,
      },
      {
        key: "settings",
        label: "Settings",
        path: "/dashboard/settings",
        icon: <SettingsIcon />,
        matchPrefixes: ["/dashboard/settings"],
      },
    ];
    return items.filter((i) => i.when !== false);
  }, [user]);

  const otherItems: NavItem[] = [
    // {
    //   key: "help",
    //   label: "Help",
    //   path: "/dashboard/help",
    //   icon: <HelpIcon />,
    //   matchPrefixes: ["/dashboard/help"],
    // },
  ];

  const advancedItems: NavItem[] = useMemo(() => {
    const base: NavItem[] = [
      {
        key: "developers",
        label: "Developers",
        path: "/dashboard/developers",
        icon: <DeveloperModeIcon />,
        matchPrefixes: ["/dashboard/developers"],
      },
      {
        key: "docs",
        label: "Docs",
        path: "https://docs.penciled.com",
        icon: <LibraryBooksIcon />,
        external: true,
      },
    ];

    if (process.env.NODE_ENV === "development") {
      base.push(
        {
          key: "notification-center",
          label: "Notification Center",
          path: "/dashboard/notifications",
          icon: <LibraryBooksIcon />,
          matchPrefixes: ["/dashboard/notifications"],
        },
        {
          key: "view-patient-plan-of-care",
          label: "View Patient Plan Of Care",
          path: "/dashboard/patient-plan-of-care/view",
          icon: <LibraryBooksIcon />,
          matchPrefixes: ["/dashboard/patient-plan-of-care/view"],
        },
      );
    }

    return base;
  }, []);

  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  // --------------------------
  // Selection helpers
  // --------------------------
  const isItemSelected = React.useCallback(
    (item: NavItem) => {
      const prefixes = item.matchPrefixes ?? [item.path];
      return prefixes.some((p) => {
        return location.pathname === p || (location.pathname.startsWith(p) && (location.pathname.length === p.length || location.pathname[p.length] === "/"));
      });
    },
    [location.pathname],
  );

  const isParentSelected = (parent: NavItem) => isItemSelected(parent);

  // --------------------------
  // Navigation handler
  // --------------------------
  const go = (item: NavItem) => {
    posthog?.capture("[PENCILED] dashboard_sidebar_item_clicked", { item: item.label });

    if (isExternal(item.path) || item.external) {
      window.open(item.path, "_blank");
    } else {
      navigate(item.path);
    }

    if (isMobile) setMobileOpen(false);
  };

  const handleClickNav = (e: React.MouseEvent, item: NavItem) => {
    // allow middle click / cmd click / ctrl click / right click
    if (e.button !== 0 || e.metaKey || e.ctrlKey) return;
    e.preventDefault();
    go(item);
  };

  // --------------------------
  // UI Renderers
  // --------------------------
  const renderNavItem = (item: NavItem, opts?: { dense?: boolean; indent?: number }) => {
    const dense = opts?.dense ?? false;
    const indent = opts?.indent ?? 0;

    const selected = isItemSelected(item);

    return (
      <React.Fragment key={item.key}>
        <ListItem disablePadding>
          <ListItemButton
            component="a"
            href={item.path}
            onClick={(e) => handleClickNav(e, item)}
            sx={{
              backgroundColor: selected ? "#2D3042" : "inherit",
              "&:hover": {
                backgroundColor: selected ? Colors.primary : "rgba(0, 0, 255, 0.1)",
              },
              borderRadius: "10px",
              margin: "5px 15px",
              pl: indent ? `${indent}px` : undefined,
            }}
          >
            <ListItemIcon sx={{ color: "white", minWidth: dense ? 36 : 56 }}>{item.icon}</ListItemIcon>
            <ListItemText primary={<Typography sx={{ fontSize: dense ? "0.85rem" : "0.95rem", color: "#FFF" }}>{item.label}</Typography>} />
          </ListItemButton>
        </ListItem>

        {/* Children: only show when parent selected (for Providers behavior) */}
        {!!item.children?.length && (!item.showChildrenOnlyWhenSelected || isParentSelected(item)) && (
          <Box sx={{ pl: 4 }}>{item.children.map((child) => renderNavItem(child, { dense: true, indent: 0 }))}</Box>
        )}
      </React.Fragment>
    );
  };

  const drawer = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", background: "#181C2F" }}>
      <Box>
        <WhitePenciledLogo style={{ width: "150px", margin: "20px 15px" }} />

        {user && user.organizations && user.organizations.length > 1 && (
          <FormControl sx={{ margin: "5px 5px 5px 15px" }}>
            <Select
              value={organization?.organizationId || ""}
              onChange={(e) => handleOrgChange(e.target.value as string)}
              inputProps={{ "aria-label": "Without label" }}
              sx={{ color: "#FFF", minWidth: "200px", maxWidth: "270px" }}
            >
              {user.organizations.map((org) => (
                <MenuItem key={org.organizationId} value={org.organizationId}>
                  {org.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <List>
          {navItems.map((item) => renderNavItem(item))}

          {otherItems.map((item) => renderNavItem(item))}

          {/* Advanced section */}
          {/* <ListItem>
            <ListItemButton onClick={() => setShowAdvancedOptions((prev) => !prev)}>
              <ListItemIcon sx={{ color: "white" }}>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText primary={<Typography sx={{ fontSize: "0.95rem", color: "#FFF" }}>Advanced</Typography>} />
              <ListItemIcon>{showAdvancedOptions ? <ExpandLessIcon /> : <ExpandMoreIcon />}</ListItemIcon>
            </ListItemButton>
          </ListItem>

          {showAdvancedOptions && <Box sx={{ pl: 4 }}>{advancedItems.map((i) => renderNavItem(i, { dense: true }))}</Box>} */}
        </List>
      </Box>

      <Box sx={{ mt: "auto", pb: 1 }}>
        <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.12)", mx: 2, my: 1 }} />
        <List>{bottomNavItems.map((item) => renderNavItem(item))}</List>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", background: "#151A32", minHeight: "100vh" }}>
      <CssBaseline />

      <AppBar position="fixed" sx={{ display: { lg: "none" }, background: Colors.bg, height: "70px" }}>
        <Toolbar style={{ display: "flex", justifyContent: "space-between", height: "70px" }}>
          <IconButton color="primary" aria-label="open drawer" edge="start" onClick={toggleMobileDrawer} sx={{ mr: 2 }}>
            <MenuIcon sx={{ fontSize: "30px" }} />
          </IconButton>

          <WhitePenciledLogo style={{ width: "125px" }} />

          <Box onClick={handleAccountClick}>
            <AccountCircleIcon style={{ fontSize: "2.1rem", cursor: "pointer", color: Colors.primary }} />
          </Box>

          <Menu anchorEl={anchorEl} open={accountMenuOpen} onClose={() => handleAccountMenuClose()} PaperProps={{ style: menuPropsStyles.PaperProps.sx }} sx={dropdownStyles}>
            <MenuItem onClick={() => handleAccountMenuClose("account")}>My account</MenuItem>
            <MenuItem onClick={() => handleAccountMenuClose("logout")}>Logout</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { lg: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={toggleMobileDrawer}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", lg: "none" },
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              border: "none",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            },
          }}
        >
          {drawer}
        </Drawer>

        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", lg: "block" },
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              border: "none",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box component="main" sx={{ flexGrow: 1, p: { xs: 3, md: 5 }, background: Colors.bg }}>
        <Outlet />
      </Box>
    </Box>
  );
};

export default DashboardSidebar;
